// Full-screen overlay shown during AI estimate. Replaces 8-15s of silent
// staring (UI audit Critical #9) with staged progress + a Cancel button that
// aborts the underlying fetch.

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import type { ProgressPhase } from "../lib/vision/types";

/** Input mode the overlay is wrapping — drives mode-aware copy in the
 *  "uploading" phase since "Uploading your photo…" makes no sense for
 *  describe / barcode / recipe flows. */
type AnalyzeMode = "photo" | "barcode" | "describe" | "recipe";

interface Props {
  phase: ProgressPhase;
  onCancel: () => void;
  /** Which input mode is being analyzed — drives copy in early phases. */
  mode?: AnalyzeMode;
  /** Optional thumbnail of the image being analyzed (photo mode only). */
  thumbnailDataUrl?: string;
}

/** Per-mode copy for the initial "uploading" phase. Photo is genuinely an
 *  upload; the other modes are sending small text payloads, so we use
 *  honest language ("Sending your meal…" etc.) instead of lying about a
 *  photo upload. Used by demo videos too — don't say "photo" when there
 *  isn't one. */
const UPLOADING_COPY_BY_MODE: Record<AnalyzeMode, { label: string; sub: string }> = {
  photo: {
    label: "Uploading your photo…",
    sub: "Sent securely. Stays on your account."
  },
  describe: {
    label: "Sending your description…",
    sub: "Sent securely. Stays on your account."
  },
  barcode: {
    label: "Looking up the barcode…",
    sub: "Sent securely. Stays on your account."
  },
  recipe: {
    label: "Sending your recipe…",
    sub: "Sent securely. Stays on your account."
  }
};

const PHASE_COPY: Record<ProgressPhase, { label: string; sub: string }> = {
  uploading: UPLOADING_COPY_BY_MODE.photo, // default; overridden below per mode
  analyzing: {
    label: "Identifying your food…",
    sub: "Working out what's on the plate and how much. Usually 3–8 seconds."
  },
  parsing: {
    label: "Calculating macros…",
    sub: "Almost done."
  },
  done: { label: "Done", sub: "" }
};

const PHASE_ORDER: ProgressPhase[] = ["uploading", "analyzing", "parsing"];

export default function AnalyzingOverlay({ phase, onCancel, mode = "photo", thumbnailDataUrl }: Props) {
  // Track elapsed time so we can show a soft "slower than usual" hint at 10s.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 500);
    return () => clearInterval(id);
  }, []);

  // Mode-aware override for the "uploading" phase; all other phases use shared copy.
  const copy = phase === "uploading" ? UPLOADING_COPY_BY_MODE[mode] : PHASE_COPY[phase];
  const phaseIndex = PHASE_ORDER.indexOf(phase);
  const showSlow = elapsed >= 10;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-4"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-5 shadow-2xl">
        {thumbnailDataUrl && (
          <img
            src={thumbnailDataUrl}
            alt=""
            className="w-full aspect-video object-cover rounded-lg bg-slate-800 opacity-80"
          />
        )}

        <div className="flex items-center gap-3">
          <span className="inline-block w-5 h-5 border-2 border-slate-600 border-t-emerald-400 rounded-full animate-spin flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-base font-semibold text-slate-100">{copy.label}</div>
            <div className="text-xs text-slate-400 mt-0.5">{copy.sub}</div>
          </div>
          <span className="text-xs text-slate-500 tabular-nums flex-shrink-0">{elapsed}s</span>
        </div>

        {/* Phase pips — show progression so the user knows it's not stuck. */}
        <div className="flex gap-1.5">
          {PHASE_ORDER.map((p, i) => (
            <div
              key={p}
              className={`flex-1 h-1 rounded-full transition-colors ${
                i < phaseIndex
                  ? "bg-emerald-500"
                  : i === phaseIndex
                  ? "bg-emerald-400/60 animate-pulse"
                  : "bg-slate-800"
              }`}
              aria-hidden="true"
            />
          ))}
        </div>

        {showSlow && (
          <div className="text-xs text-amber-300 bg-amber-950/40 border border-amber-900 rounded p-2">
            Taking longer than usual. The model server may be slow — you can wait or cancel and try again.
          </div>
        )}

        <button
          type="button"
          onClick={onCancel}
          className="w-full py-2.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm font-medium flex items-center justify-center gap-2"
        >
          <X className="w-4 h-4" />
          Cancel
        </button>
      </div>
    </div>
  );
}
